import { useCallback, useEffect, useRef, useState } from 'react';
import { apiGet, apiPost } from 'services/api';

export type StepStatus = 'pending' | 'in_progress' | 'ok' | 'fail';

export interface EpisodePipelineState {
  index: number;
  idea: string;
  skillLevel?: number;
  duration?: string;
  steps: {
    generate: StepStatus;
    save: StepStatus;
    localize: StepStatus;
    voices: StepStatus;
  };
  error?: string;
  errorStep?: string;
  episodeId?: string;
}

export interface BulkOrchestratorState {
  status: 'idle' | 'running' | 'completed';
  episodes: EpisodePipelineState[];
  succeeded: number;
  failed: number;
  startedAt?: Date;
}

export interface ParsedIdea {
  index: number;
  idea: string;
  skillLevel: number;
  duration?: string;
  title?: string;
  tagIds?: number[];
  tagNames?: string;
  learningGoalId?: number;
  learningGoalName?: string;
}

interface GeneratedEpisodeData {
  name?: string;
  context?: string;
  goals?: string;
  personas?: unknown[];
  scenario?: string;
  duration_id?: number;
  complexity_level?: number;
  tag_ids?: number[];
  thumbnail_url?: string | null;
  background_image_url?: string | null;
  dictionary?: unknown[] | null;
}

interface GenerateResponse {
  episode: GeneratedEpisodeData;
  episode_id: string;
}

const DURATION_MAP: Record<string, number> = {
  Micro: 1,
  Short: 2,
  Medium: 3,
  Long: 4,
  Extended: 5,
};

const GENERATE_TIMEOUT = 300000; // 5 minutes
const SAVE_TIMEOUT = 30000; // 30 seconds
const LOCALIZE_TIMEOUT = 300000;
const VOICES_TIMEOUT = 30000; // 30 seconds

export function useBulkEpisodeOrchestrator() {
  const [state, setState] = useState<BulkOrchestratorState>({
    status: 'idle',
    episodes: [],
    succeeded: 0,
    failed: 0,
  });

  const abortControllerRef = useRef<AbortController | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  const updateStep = useCallback(
    (
      index: number,
      step: keyof EpisodePipelineState['steps'],
      status: StepStatus,
      error?: string
    ) => {
      setState((prev) => {
        const episodes = [...prev.episodes];
        episodes[index] = {
          ...episodes[index],
          steps: { ...episodes[index].steps, [step]: status },
          ...(error ? { error, errorStep: step } : {}),
        };
        return { ...prev, episodes };
      });
    },
    []
  );

  const processEpisode = useCallback(
    async (
      idea: ParsedIdea,
      languageCode: string,
      targetLanguages: string[],
      signal: AbortSignal,
      episodeIndex: number,
      updateStepFn: typeof updateStep
    ): Promise<{ status: 'success' | 'failed'; episodeId?: string }> => {
      if (signal.aborted) return { status: 'failed' };

      // Step 1: Generate
      updateStepFn(episodeIndex, 'generate', 'in_progress');
      let generated: GenerateResponse;

      try {
        generated = await apiPost<GenerateResponse>(
          '/api/admin/generation/generate',
          {
            idea: idea.idea,
            language: languageCode,
            complexity: idea.skillLevel,
            tags: idea.tagIds?.length ? idea.tagIds : undefined,
            duration_id: idea.duration ? DURATION_MAP[idea.duration] : undefined,
            title_override: languageCode === 'en' ? idea.title || undefined : undefined,
          },
          GENERATE_TIMEOUT,
          signal
        );
        updateStepFn(episodeIndex, 'generate', 'ok');
      } catch (err) {
        if (signal.aborted) return { status: 'failed' };
        const msg = err instanceof Error ? err.message : String(err);
        updateStepFn(episodeIndex, 'generate', 'fail', msg);
        return { status: 'failed' };
      }

      const episodeId = generated.episode_id;
      const ep = generated.episode;

      // Update state with episodeId
      setState((prev) => {
        const episodes = [...prev.episodes];
        episodes[episodeIndex] = { ...episodes[episodeIndex], episodeId };
        return { ...prev, episodes };
      });

      // Step 2: Save
      updateStepFn(episodeIndex, 'save', 'in_progress');
      try {
        await apiPost(
          '/api/admin/episodes',
          {
            id: episodeId,
            name: languageCode === 'en' && idea.title ? idea.title : ep.name,
            language_code: languageCode,
            duration_id: ep.duration_id || DURATION_MAP[idea.duration || 'Medium'] || 3,
            context: ep.context,
            goals: ep.goals,
            personas: ep.personas,
            scenario: ep.scenario,
            complexity_level: ep.complexity_level || idea.skillLevel,
            tag_ids: idea.tagIds?.length ? idea.tagIds : ep.tag_ids,
            thumbnail_url: ep.thumbnail_url || null,
            background_image_url: ep.background_image_url || null,
            dictionary: ep.dictionary || null,
          },
          SAVE_TIMEOUT,
          signal
        );
        updateStepFn(episodeIndex, 'save', 'ok');
      } catch (err) {
        if (signal.aborted) return { status: 'failed' };
        const msg = err instanceof Error ? err.message : String(err);
        updateStepFn(episodeIndex, 'save', 'fail', msg);
        return { status: 'failed' };
      }

      // Step 3: Localize (non-fatal)
      if (targetLanguages.length > 0) {
        updateStepFn(episodeIndex, 'localize', 'in_progress');
        try {
          const bulkResult = await apiPost<{
            localizations: Record<string, Record<string, unknown>>;
            successful_count: number;
            failed_languages: string[];
          }>(
            `/api/admin/episodes/${episodeId}/display-localizations/generate-bulk`,
            { target_language_codes: targetLanguages },
            LOCALIZE_TIMEOUT,
            signal
          );

          // Save each successful localization
          for (const [, localization] of Object.entries(bulkResult.localizations)) {
            await apiPost(
              `/api/admin/episodes/${episodeId}/display-localizations`,
              localization,
              SAVE_TIMEOUT,
              signal
            );
          }
          updateStepFn(episodeIndex, 'localize', 'ok');
        } catch (err) {
          if (signal.aborted) return { status: 'failed' };
          const msg = err instanceof Error ? err.message : String(err);
          updateStepFn(episodeIndex, 'localize', 'fail', msg);
          // Non-fatal — continue to voices
        }
      } else {
        updateStepFn(episodeIndex, 'localize', 'ok');
      }

      // Step 4: Voice Group (non-fatal)
      updateStepFn(episodeIndex, 'voices', 'in_progress');
      try {
        const suggestions = await apiGet<{
          suggested_voice_assignments: { voice_id: number }[];
          narrator_voice_id: number | null;
          has_narrator: boolean;
        }>(`/api/admin/episodes/${episodeId}/suggest-voices`, undefined, VOICES_TIMEOUT);

        const voiceIds = suggestions.suggested_voice_assignments.map(
          (s: { voice_id: number }) => s.voice_id
        );
        if (suggestions.narrator_voice_id) {
          voiceIds.unshift(suggestions.narrator_voice_id);
        }

        // Validate: voice count must match persona count
        const personaCount = ep.personas?.length ?? 0;
        if (voiceIds.length !== personaCount) {
          throw new Error(
            `Voice count mismatch: got ${voiceIds.length} voices for ${personaCount} personas`
          );
        }

        await apiPost(
          `/api/admin/episodes/${episodeId}/voice-groups`,
          { locale_code: null, voice_ids: voiceIds },
          VOICES_TIMEOUT,
          signal
        );
        updateStepFn(episodeIndex, 'voices', 'ok');
      } catch (err) {
        if (signal.aborted) return { status: 'failed' };
        const msg = err instanceof Error ? err.message : String(err);
        updateStepFn(episodeIndex, 'voices', 'fail', msg);
        // Non-fatal
      }

      return { status: 'success', episodeId };
    },
    []
  );

  const start = useCallback(
    async (
      ideas: ParsedIdea[],
      languageCode: string,
      concurrency: number,
      targetLanguages: string[]
    ) => {
      const controller = new AbortController();
      abortControllerRef.current = controller;

      const initialEpisodes: EpisodePipelineState[] = ideas.map((idea, i) => ({
        index: i,
        idea: idea.idea,
        skillLevel: idea.skillLevel,
        duration: idea.duration,
        steps: { generate: 'pending', save: 'pending', localize: 'pending', voices: 'pending' },
      }));

      setState({
        status: 'running',
        episodes: initialEpisodes,
        succeeded: 0,
        failed: 0,
        startedAt: new Date(),
      });

      let succeeded = 0;
      let failed = 0;
      let nextIndex = 0;

      const workers: Promise<void>[] = [];

      for (let w = 0; w < concurrency; w++) {
        workers.push(
          (async () => {
            while (nextIndex < ideas.length && !controller.signal.aborted) {
              const idx = nextIndex++;
              const result = await processEpisode(
                ideas[idx],
                languageCode,
                targetLanguages,
                controller.signal,
                idx,
                updateStep
              );
              if (result.status === 'success') {
                succeeded++;
              } else {
                failed++;
              }
              setState((prev) => ({ ...prev, succeeded, failed }));
            }
          })()
        );
      }

      await Promise.all(workers);
      setState((prev) => ({ ...prev, status: 'completed' }));
    },
    [processEpisode, updateStep]
  );

  const abort = useCallback(() => {
    abortControllerRef.current?.abort();
    setState((prev) => ({ ...prev, status: 'completed' }));
  }, []);

  return { state, start, abort };
}
